<template>
    <div class="form__container">
        <!-- Submission Success Message -->
        <div v-if="submitted" class="form__submitted-message">
            <h3 class="form__submitted-title font-heading">Thank you for your enquiry</h3>
            <p class="form__submitted-message">
                Hold tight, one of our team will be in touch with you very shortly!
            </p>
        </div>

    

        <!-- Form Steps -->
        <form
            ref="formContainer"
            v-if="!submitted"
            :class="[formClass, { shake: shake }]"
            @submit.prevent="submitForm"
        >
            <div class="w-full" v-if="title || description">
                <span v-if="title" class="form__title font-heading">{{ title }}</span>

                <span
                    class="form__description font-normal text-baselg:text-lg block"
                    v-if="description"
                    >{{ description }}</span
                >
            </div>

            <template
                v-for="(item, itemIndex) in form.items"
                :key="'item_' + itemIndex"
            >
                <div
                    v-if="
                        item.object_type == 'row' && shouldFieldBeVisible(item)
                    "
                    :class="item.class"
                    class="w-full"
                >
                    <div
                        v-for="(item, itemIndex) in item.items"
                        :key="'subItem_' + itemIndex"
                    >
                        <template
                            v-if="
                                item.object_type == 'field' &&
                                shouldFieldBeVisible(item)
                            "
                        >
                            <InputField
                                v-model="form.data[item.id]"
                                :label="item.label"
                                :name="`${item.name}`"
                                :type="item.field_type"
                                :rules="item.rules"
                                :description="item.description"
                                :options="item.options"
                                :placeholder="item.placeholder"
                                class="form__input-field"
                                :multiple="item.is_multiple"
                            />

                            <span
                                v-if="item.description"
                                class="text-xs text-secondary"
                                v-html="item.description"
                            ></span>
                        </template>
                    </div>
                </div>

                <template
                    v-if="
                        item.object_type == 'field' &&
                        shouldFieldBeVisible(item)
                    "
                >
                    <InputField
                        v-model="form.data[item.id]"
                        :label="item.label"
                        :name="`${item.name}`"
                        :type="item.field_type"
                        :rules="item.rules"
                        :description="item.description"
                        :options="item.options"
                        :placeholder="item.placeholder"
                        :multiple="item.is_multiple"
                        class="form__input-field w-full"
                    />
                </template>

                <fieldset
                    v-if="
                        item.object_type == 'step' && shouldFieldBeVisible(item)
                    "
                    :class="fieldsetClass"
                >
                    <legend
                        v-if="showLegend"
                        class="text-lg font-medium py-2 px-4 bg-primary text-white rounded-lg"
                    >
                        {{ item.label }}
                    </legend>

                    <!-- SUBITEMS -->
                    <div class="flex flex-col gap-4" :class="item.class">
                        <div
                            v-for="(item, itemIndex) in item.items"
                            :key="'subItem_' + itemIndex"
                        >
                            <div
                                v-if="
                                    item.object_type == 'row' &&
                                    shouldFieldBeVisible(item)
                                "
                                :class="item.class"
                            >
                                <div
                                    v-for="(item, itemIndex) in item.items"
                                    :key="'subItem_' + itemIndex"
                                    class="w-full!"
                                >
                                    <template
                                        v-if="
                                            item.object_type == 'field' &&
                                            shouldFieldBeVisible(item)
                                        "
                                    >
                                        <InputField
                                            v-model="form.data[item.id]"
                                            :label="item.label"
                                            :name="`${item.name}`"
                                            :type="item.field_type"
                                            :rules="item.rules"
                                            :description="item.description"
                                            :placeholder="item.placeholder"
                                            :options="item.options"
                                            :multiple="item.is_multiple"
                                            class="form__input-field"
                                        />
                                    </template>
                                </div>
                            </div>

                            <div
                                v-if="
                                    item.object_type == 'field' &&
                                    shouldFieldBeVisible(item)
                                "
                            >
                                <InputField
                                    v-model="form.data[item.id]"
                                    :label="item.label"
                                    :name="`${item.name}`"
                                    :type="item.field_type"
                                    :rules="item.rules"
                                    :description="item.description"
                                    :placeholder="item.placeholder"
                                    :options="item.options"
                                    :multiple="item.is_multiple"
                                    class="form__input-field"
                                />
                            </div>
                        </div>
                    </div>
                    <!--    END SUBITEM -->
                </fieldset>
                <!-- END STEP -->
            </template>

            <div
                v-if="form.enable_turnstile"
                class="flex justify-center items-center"
            >
                <TurnstileWidget
                    :siteKey="siteKey"
                    @success="handleTurnstileSuccess"
                    @error="handleTurnstileError"
                />
            </div>
            <div
                v-if="turnstileError && form.enable_turnstile"
                class="form__error-message"
            >
                {{ turnstileError }}
            </div>

            <!-- Submit Button -->
            <div class="form__buttons">
                <button
                    type="submit"
                    class="form__button-submit"
                    :disabled="!canSubmit"
                >
                    <span v-if="!submitting">Send message</span
                    ><span class="text-white" v-else>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                fill="currentColor"
                                class="spinner"
                                cx="4"
                                cy="12"
                                r="1.5"
                            />
                            <circle
                                fill="currentColor"
                                class="spinner delay"
                                cx="12"
                                cy="12"
                                r="3"
                            />
                            <circle
                                fill="currentColor"
                                class="spinner"
                                cx="20"
                                cy="12"
                                r="1.5"
                            />
                        </svg>
                    </span>
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import {
    ref,
    computed,
    onMounted,
    defineAsyncComponent,
    nextTick,
    inject,
} from "vue";
import { useForm, configure } from "vee-validate";

import InputField from "@helpers/Forms/InputField.vue";

import TurnstileWidget from "@components/Integrations/Turnstile/Turnstile.vue";

const formContainer = ref(null);

// Props
const props = defineProps({
    form: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(["submitted"]);

// Provided injecatables
const title = inject("title");
const description = inject("description");
const showLegend = inject("showLegend");
const formClass = inject("formClass");
const fieldsetClass = inject("fieldsetClass");

const { handleSubmit } = useForm();

const submitted = ref(false);

// Initialize form data with Turnstile response
const form = ref(props.form);

// Get content ofr meta "turnstile-site-key" from header
// Select the meta tag
const siteKey = ref("");

// Turnstile Validation State
const turnstileValid = ref(false);
const turnstileError = ref("");

const canSubmit = computed(() => {
    if (form.value.enable_turnstile) {
        return turnstileValid.value && !submitting.value;
    }

    return !submitting.value;
});

// Handle Turnstile Success Callback
const handleTurnstileSuccess = (token) => {
    form.value.data["cf-turnstile-response"] = token;
    turnstileValid.value = true;
    turnstileError.value = "";
    console.log("Turnstile verification succeeded with token:", token);
};

// Handle Turnstile Error Callback
const handleTurnstileError = () => {
    turnstileValid.value = false;
    turnstileError.value = "Turnstile verification failed. Please try again.";
    console.error("Turnstile verification failed.");
};

const submitting = ref(false);

// New reactive property to trigger shake
const shake = ref(false);

// Helper function to trigger the shake animation
function shakeForm() {
    shake.value = true;

    // Reset the shake state after the animation duration (500ms)
    setTimeout(() => {
        shake.value = false;
    }, 500);
}

// This function checks whether a field should be visible
const shouldFieldBeVisible = (field) => {
    // If there are no rules, the field is always visible.
    if (
        !field.conditional_logic ||
        !field.conditional_logic.rules ||
        field.conditional_logic.rules.length === 0
    ) {
        return true;
    }

    if (!field.enable_conditional_logic) {
        return true;
    }

    const { action, match, rules } = field.conditional_logic;
    // Map each rule to a boolean result.
    const ruleResults = rules.map((rule) => {
        // Get the current value of the referenced field from the form data.
        // (Assuming your form data is stored in an object called "form.data")

        const compareField = form.value.data[rule.field];

        // Evaluate based on the operator.
        switch (rule.operator) {
            case "is":
                return compareField == rule.value;
            case "is_not":
                return compareField != rule.value;
            case "greater_than":
                return parseFloat(compareField) > parseFloat(rule.value);
            case "less_than":
                return parseFloat(compareField) < parseFloat(rule.value);
            case "contains":
                return String(compareField).includes(rule.value);
            case "starts_with":
                return String(compareField).startsWith(rule.value);
            case "ends_with":
                return String(compareField).endsWith(rule.value);
            default:
                return false;
        }
    });

    // Combine the results based on the match type.
    let rulesPassed =
        match === "all"
            ? ruleResults.every((result) => result)
            : ruleResults.some((result) => result);

    // For a "show" action, show the field if rulesPassed is true.
    // For a "hide" action, show the field only if rulesPassed is false.
    return action === "show" ? rulesPassed : !rulesPassed;
};

const submitForm = handleSubmit(() => {
    submit();
});

const submit = async () => {
    try {
        submitting.value = true;
        const { default: axios } = await import("axios");

        // Build the payload as JSON.
        // Assuming form.value.data already contains the structured file objects.
        const payload = {
            entries: form.value.data,
        };

        // Submit the JSON payload via axios.
        const response = await axios.post(form.value.endpoint, payload, {
            headers: { "Content-Type": "application/json" },
        });

        if (response.status === 200) {
            submitted.value = true;
            form.value.data = {};
            if (formContainer.value) {
                formContainer.value.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        } else {
            console.error("Unexpected response status:", response.status);
            shakeForm();
            turnstileError.value = "An unexpected error occurred.";
        }
    } catch (error) {
        console.error("Form submission error:", error);
        shakeForm();
        formError.value = error.response.data?.message || "An error occurred.";
    } finally {
        submitting.value = false;
    }
};

onMounted(() => {
    if (form.value.enable_turnstile) {
        // Get the Turnstile site key from the meta tag
        const meta = document.querySelector('meta[name="turnstile-site-key"]');
        if (meta) {
            siteKey.value = meta.content;
        }
    }
});

// Configure Vee-validate
configure({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false,
});
</script>

<style scoped>
@keyframes spinner {
    0%,
    100% {
        r: 1.5px;
    }
    50% {
        r: 3px;
    }
}
.spinner {
    animation: spinner 0.75s linear infinite;
}
.delay {
    animation-delay: -0.375s;
}

@keyframes shake {
    0% {
        transform: translateX(0);
    }
    20% {
        transform: translateX(-10px);
    }
    40% {
        transform: translateX(10px);
    }
    60% {
        transform: translateX(-10px);
    }
    80% {
        transform: translateX(10px);
    }
    100% {
        transform: translateX(0);
    }
}

.shake {
    animation: shake 0.5s ease;
}
</style>
